import type { FC, ReactNode } from 'react';
import { lazy, Suspense } from 'react';
import { Toaster } from 'sonner';
import { AuctionTilesTraffic } from 'src/apis/transport-auction/auction-tiles-traffic';
import { FadeIn } from 'src/components/animation/fade-in';
import { AuthSessionMonitor } from 'src/components/auth-session-monitor';
import { CookieCheck } from 'src/components/cookie-check';
import { ErrorBoundary } from 'src/components/feedback/error-boundary';
import { FatalErrorBoundary } from 'src/components/feedback/fatal-error-boundary';
import { AppShell } from 'src/components/layout/app-shell';
import { ScrollToTopButton } from 'src/components/layout/app-shell/scroll-to-top-button';
import { AppShellProvider } from 'src/components/layout/app-shell/use-app-shell-provider';
import { Container } from 'src/components/layout/container';
import { Footer } from 'src/components/layout/footer';
import { Header } from 'src/components/layout/header';
import { Layout, LayoutFooter, LayoutMain } from 'src/components/layout/layout';
import { SimplePageLayout } from 'src/components/layout/simple-page-layout';
import { AngularModalDimmer } from 'src/components/overlay/angular-modal-dimmer';
import { CookieBanner } from 'src/components/overlay/cookie-banner';
import { useWelcomeModal } from 'src/components/overlay/use-welcome-modal';
import { ScrollManagerProvider } from 'src/components/scroll-management';
import { ScrollToTop } from 'src/components/scroll-to-top';
import { PageSpinner } from 'src/components/spinner-container';
import { WizardsLiftetime } from 'src/components/wizard/use-wizard-store';
import { GlobalResourcesProvider } from 'src/global-resources-provider';
import { AccountsProvider } from 'src/hooks/use-accounts';
import {
  AxiosHookConfigProvider,
  TransformAxiosConfig,
} from 'src/hooks/use-axios';
import { BreakpointsProvider } from 'src/hooks/use-breakpoints';
import { CognitoUserProvider } from 'src/hooks/use-cognito-user';
import { DropManagerProvider } from 'src/hooks/use-drop-manager';
import { FullwidthProvider } from 'src/hooks/use-fullwidth';
import { HeaderHeightProvider } from 'src/hooks/use-header-height';
import { useShowHelpAnnotations } from 'src/hooks/use-help-annotations';
import { useInitialCleanup } from 'src/hooks/use-initial-cleanup';
import { IsPrintProvider } from 'src/hooks/use-is-print';
import { useOurProfiler } from 'src/hooks/use-our-profiler';
import { PreviousLocationProvider } from 'src/hooks/use-previous-location';
import { useSonnerErrorDuration } from 'src/hooks/use-sonner-error-duration';
import { useStorybook } from 'src/hooks/use-storybook';
import { useDeveloperTools } from 'src/pages/developer-tools/use-developer-tools';
import { useIsNewDesignActive } from 'src/pages/new-design/is-new-design';
import { OurRoutes } from 'src/routes';
import { PrintStyle } from 'src/styles';
import { useCustomAxiosConfig } from 'src/utils/axios-config';
import { cn } from 'src/utils/cn';
import { sonnerConfig } from 'src/utils/sonner';

const DataTestidHighligher = lazy(
  () => import('src/pages/developer-tools/data-testid-highlighter')
);
const HelpAnnotations = lazy(
  () => import('src/components/domain-specifics/help-annotations')
);
const WelcomeModal = lazy(() => import('src/components/overlay/welcome-modal'));

const LayoutSelector = ({
  isNewDesignActive,
  children,
}: {
  isNewDesignActive: boolean;
  children: ReactNode;
}) => {
  if (isNewDesignActive) {
    return <AppShell>{children}</AppShell>;
  }

  return (
    <Layout>
      <Header />

      <AngularModalDimmer clipAngular>
        <LayoutMain data-testid="page-content">
          <Container>{children}</Container>
          <ScrollToTopButton />
        </LayoutMain>
      </AngularModalDimmer>

      <LayoutFooter data-testid="layout-footer">
        <Footer />
      </LayoutFooter>
    </Layout>
  );
};

const CheckWelcomeModal = () => {
  const { shouldShowWelcomeModal } = useWelcomeModal();
  return shouldShowWelcomeModal ? <WelcomeModal /> : null;
};

export const App: FC = () => {
  useInitialCleanup();
  const { isStorybook } = useStorybook();
  const axiosHookConfig = useCustomAxiosConfig();
  useOurProfiler('App');
  const developerTools = useDeveloperTools();
  const showHelpAnnotations = useShowHelpAnnotations();
  const isNewDesignActive = useIsNewDesignActive();
  useSonnerErrorDuration();
  return (
    <AppShellProvider>
      <AxiosHookConfigProvider value={axiosHookConfig}>
        <Toaster
          {...(!isNewDesignActive
            ? { ...sonnerConfig, offset: { top: '100px', right: '20px' } }
            : sonnerConfig)}
        />

        <PrintStyle />

        <PreviousLocationProvider>
          <DropManagerProvider>
            <IsPrintProvider>
              <BreakpointsProvider>
                <HeaderHeightProvider>
                  <FullwidthProvider>
                    <Suspense
                      fallback={
                        <SimplePageLayout>
                          <PageSpinner />
                        </SimplePageLayout>
                      }
                    >
                      <FatalErrorBoundary>
                        <ScrollToTop />

                        <CookieCheck />

                        <CognitoUserProvider>
                          <AccountsProvider>
                            <AuthSessionMonitor />

                            <WizardsLiftetime>
                              <GlobalResourcesProvider>
                                <LayoutSelector
                                  isNewDesignActive={isNewDesignActive}
                                >
                                  <FadeIn>
                                    <ScrollManagerProvider>
                                      <Suspense fallback={<PageSpinner />}>
                                        <ErrorBoundary>
                                          <AuctionTilesTraffic />

                                          <CheckWelcomeModal />

                                          <TransformAxiosConfig
                                            transform={(config) => ({
                                              ...config,
                                              // every request that happens within the
                                              // routes will be flagged with this section
                                              requestSection: 'page-content',
                                            })}
                                          >
                                            <OurRoutes />
                                          </TransformAxiosConfig>
                                        </ErrorBoundary>
                                      </Suspense>
                                    </ScrollManagerProvider>
                                  </FadeIn>
                                </LayoutSelector>

                                {!isStorybook && (
                                  <div
                                    className={cn(`fixed bottom-0 z-2 w-full`)}
                                  >
                                    <CookieBanner />
                                  </div>
                                )}

                                {developerTools && (
                                  <Suspense fallback={null}>
                                    <DataTestidHighligher />
                                  </Suspense>
                                )}

                                {showHelpAnnotations && (
                                  <Suspense fallback={null}>
                                    <HelpAnnotations />
                                  </Suspense>
                                )}
                              </GlobalResourcesProvider>
                            </WizardsLiftetime>
                          </AccountsProvider>
                        </CognitoUserProvider>
                      </FatalErrorBoundary>
                    </Suspense>
                  </FullwidthProvider>
                </HeaderHeightProvider>
              </BreakpointsProvider>
            </IsPrintProvider>
          </DropManagerProvider>
        </PreviousLocationProvider>
      </AxiosHookConfigProvider>
    </AppShellProvider>
  );
};
